/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { contentModerationService } from "@src/@api-services/contentModeration";
import { getModerationSubmittedByLabel, MODERATION_ACCOUNT_STATUS, MODERATION_STATUS } from "../constants";
import type {
  IContentReport,
  IContentReportDetail,
  IModerationDetail,
  IModerationQueueItem,
  MassCleanupBody,
  ModerationQueueSearchOptions,
  ReportSearchOptions,
} from "../types";

/** Prefer content author for Submitted By (not the admin acting on the queue). */
const normalizeModerationQueueItem = (raw: any): IModerationQueueItem => ({
  ...raw,
  submittedByName: getModerationSubmittedByLabel(raw, "N/A"),
});

const normalizeModerationDetail = (raw: any): IModerationDetail => ({
  ...raw,
  submittedByName: getModerationSubmittedByLabel(raw, "Unknown user"),
});

interface QueueState {
  data: IModerationQueueItem[];
  total: number;
  params: ModerationQueueSearchOptions;
  selectedItem: IModerationDetail | null;
  loading: boolean;
  detailLoading: boolean;
  actionLoading: boolean;
  error: string | null;
}

interface ReportsState {
  data: IContentReport[];
  total: number;
  params: ReportSearchOptions;
  selectedReport: IContentReportDetail | null;
  loading: boolean;
  detailLoading: boolean;
  actionLoading: boolean;
  error: string | null;
}

interface ContentModerationState {
  queue: QueueState;
  reports: ReportsState;
}

const defaultQueueParams: ModerationQueueSearchOptions = {
  page: 1,
  pageSize: 10,
  status: MODERATION_STATUS.PENDING,
  orderBy: "createdAt",
  orderDirection: "DESC",
};

const defaultReportParams: ReportSearchOptions = {
  page: 1,
  pageSize: 10,
  orderBy: "createdAt",
  orderDirection: "DESC",
};

export const getModerationQueue = createAsyncThunk(
  "contentModeration/getModerationQueue",
  async (params: ModerationQueueSearchOptions, { rejectWithValue }) => {
    try {
      const response = await contentModerationService.getModerationQueue(params);
      const payload = response.data.data;
      const rows = Array.isArray(payload?.data) ? payload.data : [];
      return {
        params,
        data: rows.map(normalizeModerationQueueItem),
        total: (payload?.totalRecord ?? payload?.total ?? 0) as number,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch review queue"
      );
    }
  }
);

export const getModerationDetail = createAsyncThunk(
  "contentModeration/getModerationDetail",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await contentModerationService.getModerationById(id);
      return normalizeModerationDetail(response.data.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch moderation details"
      );
    }
  }
);

export const approveModeration = createAsyncThunk(
  "contentModeration/approveModeration",
  async (id: number, { dispatch, getState, rejectWithValue }) => {
    try {
      await contentModerationService.approveModeration(id);
      const state = getState() as any;
      await dispatch(getModerationQueue(state.contentModeration.queue.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to approve content"
      );
    }
  }
);

export const rejectModeration = createAsyncThunk(
  "contentModeration/rejectModeration",
  async (
    { id, reason }: { id: number; reason: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      await contentModerationService.rejectModeration(id, { reason });
      const state = getState() as any;
      await dispatch(getModerationQueue(state.contentModeration.queue.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject content"
      );
    }
  }
);

export const bulkModerate = createAsyncThunk(
  "contentModeration/bulkModerate",
  async (
    {
      ids,
      action,
      reason,
    }: { ids: number[]; action: "approve" | "reject"; reason?: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await contentModerationService.bulkModerate({
        ids,
        action,
        reason,
      });
      const state = getState() as any;
      await dispatch(getModerationQueue(state.contentModeration.queue.params));
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to process bulk moderation"
      );
    }
  }
);

export const massCleanup = createAsyncThunk(
  "contentModeration/massCleanup",
  async (body: MassCleanupBody, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await contentModerationService.massCleanup(body);
      const state = getState() as any;
      await dispatch(getModerationQueue(state.contentModeration.queue.params));
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to run mass cleanup"
      );
    }
  }
);

const getPersonDisplayName = (person?: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
} | null) => {
  if (!person) return "";
  const name = [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
  return name || person.email || "";
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/** Resolve ban target from contentOwner or nested entityData (e.g. post author). */
const resolveContentOwnerId = (raw: any): number | null => {
  if (raw?.contentOwnerId != null && raw.contentOwnerId !== "") {
    return Number(raw.contentOwnerId);
  }
  if (raw?.contentOwner?.id != null) {
    return Number(raw.contentOwner.id);
  }

  const entity = isRecord(raw?.entityData) ? raw.entityData : null;
  if (!entity) return null;

  const author = isRecord(entity.author) ? entity.author : null;
  if (author?.id != null) return Number(author.id);

  const reviewer = isRecord(entity.reviewer) ? entity.reviewer : null;
  if (reviewer?.id != null) return Number(reviewer.id);

  if (entity.userId != null) return Number(entity.userId);

  return null;
};

const resolveContentOwner = (raw: any, ownerId: number | null) => {
  if (raw?.contentOwner) return raw.contentOwner;
  if (ownerId == null) return null;

  const entity = isRecord(raw?.entityData) ? raw.entityData : null;
  const person =
    (entity && isRecord(entity.author) && entity.author) ||
    (entity && isRecord(entity.reviewer) && entity.reviewer) ||
    null;

  if (!person) return null;

  return {
    id: ownerId,
    firstName: (person.firstName as string) || "",
    lastName: (person.lastName as string) || "",
    email: (person.email as string) || "",
    status: (person.status as string) || MODERATION_ACCOUNT_STATUS.ACTIVE,
  };
};

/** Map list/detail API rows — use `reporter` name/email, not raw reportedBy id. */
const normalizeContentReport = (raw: any): IContentReport => {
  const reporter = raw?.reporter ?? null;
  const reportedByName =
    raw?.reportedByName || getPersonDisplayName(reporter) || "N/A";
  const reportedByEmail =
    raw?.reportedByEmail || reporter?.email || null;

  return {
    ...raw,
    reporter,
    reportedByName,
    reportedByEmail,
  } as IContentReport;
};

const normalizeContentReportDetail = (raw: any): IContentReportDetail => {
  const base = normalizeContentReport(raw);
  const contentOwnerId = resolveContentOwnerId(raw);

  return {
    ...base,
    resolvedByName: raw?.resolvedByName ?? null,
    entityData: raw?.entityData ?? null,
    contentOwnerId,
    contentOwner: resolveContentOwner(raw, contentOwnerId),
    restrictions: Array.isArray(raw?.restrictions) ? raw.restrictions : [],
  };
};

export const getReports = createAsyncThunk(
  "contentModeration/getReports",
  async (params: ReportSearchOptions, { rejectWithValue }) => {
    try {
      const response = await contentModerationService.getReports(params);
      const payload = response.data.data;
      const rows = Array.isArray(payload?.data) ? payload.data : [];
      return {
        params,
        data: rows.map(normalizeContentReport),
        total: (payload?.totalRecord ?? payload?.total ?? 0) as number,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch reported content"
      );
    }
  }
);

export const getReportDetail = createAsyncThunk(
  "contentModeration/getReportDetail",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await contentModerationService.getReportById(id);
      return normalizeContentReportDetail(response.data.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch report details"
      );
    }
  }
);

export const updateReportStatus = createAsyncThunk(
  "contentModeration/updateReportStatus",
  async (
    {
      id,
      status,
      resolutionNotes,
    }: { id: number; status: string; resolutionNotes?: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      await contentModerationService.updateReportStatus(id, {
        status,
        resolutionNotes: resolutionNotes || null,
      });
      const state = getState() as any;
      await dispatch(getReports(state.contentModeration.reports.params));
      await dispatch(getReportDetail(id));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update report status"
      );
    }
  }
);

export const removeReportedContent = createAsyncThunk(
  "contentModeration/removeReportedContent",
  async (id: number, { dispatch, getState, rejectWithValue }) => {
    try {
      await contentModerationService.deleteReportedContent(id);
      const state = getState() as any;
      await dispatch(getReports(state.contentModeration.reports.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to remove content"
      );
    }
  }
);

export const restrictUserFromReport = createAsyncThunk(
  "contentModeration/restrictUserFromReport",
  async (
    {
      reportId,
      userId,
      restrictionType,
      reason,
      endsAt,
    }: {
      reportId: number;
      userId: number;
      restrictionType: string;
      reason?: string;
      endsAt?: string;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      await contentModerationService.restrictUserFromReport(reportId, {
        userId,
        restrictionType,
        reason: reason || null,
        endsAt: endsAt || null,
      });
      const state = getState() as any;
      await dispatch(getReports(state.contentModeration.reports.params));
      await dispatch(getReportDetail(reportId));
      return reportId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to restrict user"
      );
    }
  }
);

export const banUserFromReport = createAsyncThunk(
  "contentModeration/banUserFromReport",
  async (
    { reportId, userId, reason }: { reportId: number; userId: number; reason?: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      await contentModerationService.banUserFromReport(reportId, {
        userId,
        reason: reason || null,
      });
      const state = getState() as any;
      await dispatch(getReports(state.contentModeration.reports.params));
      await dispatch(getReportDetail(reportId));
      return reportId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to ban user"
      );
    }
  }
);

const initialState: ContentModerationState = {
  queue: {
    data: [],
    total: 0,
    params: defaultQueueParams,
    selectedItem: null,
    loading: false,
    detailLoading: false,
    actionLoading: false,
    error: null,
  },
  reports: {
    data: [],
    total: 0,
    params: defaultReportParams,
    selectedReport: null,
    loading: false,
    detailLoading: false,
    actionLoading: false,
    error: null,
  },
};

export const contentModerationSlice = createSlice({
  name: "contentModeration",
  initialState,
  reducers: {
    clearQueueError: (state) => {
      state.queue.error = null;
    },
    clearReportsError: (state) => {
      state.reports.error = null;
    },
    clearSelectedModeration: (state) => {
      state.queue.selectedItem = null;
    },
    clearSelectedReport: (state) => {
      state.reports.selectedReport = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getModerationQueue.pending, (state) => {
        state.queue.loading = true;
        state.queue.error = null;
      })
      .addCase(getModerationQueue.fulfilled, (state, action) => {
        state.queue.loading = false;
        state.queue.data = action.payload.data;
        state.queue.total = action.payload.total;
        state.queue.params = action.payload.params;
      })
      .addCase(getModerationQueue.rejected, (state, action) => {
        state.queue.loading = false;
        state.queue.error = action.payload as string;
        state.queue.data = [];
        state.queue.total = 0;
      })
      .addCase(getModerationDetail.pending, (state) => {
        state.queue.detailLoading = true;
      })
      .addCase(getModerationDetail.fulfilled, (state, action) => {
        state.queue.detailLoading = false;
        state.queue.selectedItem = action.payload;
      })
      .addCase(getModerationDetail.rejected, (state) => {
        state.queue.detailLoading = false;
        state.queue.selectedItem = null;
      })
      .addCase(approveModeration.pending, (state) => {
        state.queue.actionLoading = true;
      })
      .addCase(approveModeration.fulfilled, (state) => {
        state.queue.actionLoading = false;
        state.queue.selectedItem = null;
      })
      .addCase(approveModeration.rejected, (state) => {
        state.queue.actionLoading = false;
      })
      .addCase(rejectModeration.pending, (state) => {
        state.queue.actionLoading = true;
      })
      .addCase(rejectModeration.fulfilled, (state) => {
        state.queue.actionLoading = false;
        state.queue.selectedItem = null;
      })
      .addCase(rejectModeration.rejected, (state) => {
        state.queue.actionLoading = false;
      })
      .addCase(bulkModerate.pending, (state) => {
        state.queue.actionLoading = true;
      })
      .addCase(bulkModerate.fulfilled, (state) => {
        state.queue.actionLoading = false;
      })
      .addCase(bulkModerate.rejected, (state) => {
        state.queue.actionLoading = false;
      })
      .addCase(massCleanup.pending, (state) => {
        state.queue.actionLoading = true;
      })
      .addCase(massCleanup.fulfilled, (state) => {
        state.queue.actionLoading = false;
      })
      .addCase(massCleanup.rejected, (state) => {
        state.queue.actionLoading = false;
      })
      .addCase(getReports.pending, (state) => {
        state.reports.loading = true;
        state.reports.error = null;
      })
      .addCase(getReports.fulfilled, (state, action) => {
        state.reports.loading = false;
        state.reports.data = action.payload.data;
        state.reports.total = action.payload.total;
        state.reports.params = action.payload.params;
      })
      .addCase(getReports.rejected, (state, action) => {
        state.reports.loading = false;
        state.reports.error = action.payload as string;
        state.reports.data = [];
        state.reports.total = 0;
      })
      .addCase(getReportDetail.pending, (state) => {
        state.reports.detailLoading = true;
      })
      .addCase(getReportDetail.fulfilled, (state, action) => {
        state.reports.detailLoading = false;
        state.reports.selectedReport = action.payload;
      })
      .addCase(getReportDetail.rejected, (state) => {
        state.reports.detailLoading = false;
        state.reports.selectedReport = null;
      })
      .addCase(updateReportStatus.pending, (state) => {
        state.reports.actionLoading = true;
      })
      .addCase(updateReportStatus.fulfilled, (state) => {
        state.reports.actionLoading = false;
      })
      .addCase(updateReportStatus.rejected, (state) => {
        state.reports.actionLoading = false;
      })
      .addCase(removeReportedContent.pending, (state) => {
        state.reports.actionLoading = true;
      })
      .addCase(removeReportedContent.fulfilled, (state) => {
        state.reports.actionLoading = false;
      })
      .addCase(removeReportedContent.rejected, (state) => {
        state.reports.actionLoading = false;
      })
      .addCase(restrictUserFromReport.pending, (state) => {
        state.reports.actionLoading = true;
      })
      .addCase(restrictUserFromReport.fulfilled, (state) => {
        state.reports.actionLoading = false;
      })
      .addCase(restrictUserFromReport.rejected, (state) => {
        state.reports.actionLoading = false;
      })
      .addCase(banUserFromReport.pending, (state) => {
        state.reports.actionLoading = true;
      })
      .addCase(banUserFromReport.fulfilled, (state) => {
        state.reports.actionLoading = false;
      })
      .addCase(banUserFromReport.rejected, (state) => {
        state.reports.actionLoading = false;
      });
  },
});

export const {
  clearQueueError,
  clearReportsError,
  clearSelectedModeration,
  clearSelectedReport,
} = contentModerationSlice.actions;

export default contentModerationSlice.reducer;
